/* eslint-disable sonarjs/cognitive-complexity */
import { ButtonLink, Loader, RiRoadMapLine, Stack } from '@landler/component-library';
import { centerOfMass, feature as turfFeature, featureCollection } from '@turf/turf';
import { Feature, Point } from 'geojson';
import { GeoJSONSource, LngLatBoundsLike, LngLatLike, MapLayerMouseEvent } from 'mapbox-gl';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Layer, LayerProps, MapRef, Source } from 'react-map-gl';

import { MAP_LAYER_FACTS } from '@/api/rest/resources/types/fact';
import { Plot, PlotType } from '@/api/rest/resources/types/plot';
import { Form, Map } from '@/components';
import {
  MapRadiusContext,
  NO_RADIUS,
  RadiusType,
  useMapRadiusContext,
} from '@/components/MapOverview/components/MapRadiusContextProvider';
import { MapSettings } from '@/components/MapOverview/components/MapSettings';
import { initializeMapZoom } from '@/components/MapOverview/components/ReportingBoundsLayer';
import { useBoundingBox } from '@/components/MapOverview/hooks/useBoundingBox';
import { usePlotGroupWithoutGrouping } from '@/components/PlotGroups/PlotGroupsContextProvider';
import { withSuspenseBoundary } from '@/components/SuspenseBoundary';
import { LAYER_OPTIONS, LayerProps as HeatMapLayerProps } from '@/components/widgets/o/Map/constants';
// eslint-disable-next-line import/no-cycle
import { HeatMapLayer } from '@/components/widgets/o/Map/HeatMapLayer';
import { LayerTooltip } from '@/components/widgets/o/Map/hooks/LayerTooltip';
import {
  DEFAULT_MAP_BACKGROUND,
  DEFAULT_PLOT_OVERVIEW_SHAPE,
  getDefaultOpacityByLayer,
  PLOT_OVERVIEW_SHAPE,
  PlotOverviewShape,
  useControlsContext,
  useControlsForm,
} from '@/components/widgets/o/Map/hooks/useFormControls';
import { INTERACTIVE_HEATMAP_LAYER_ID } from '@/components/widgets/o/Map/MapLayer';
import { DEFAULT_MAP_STYLE, MAP_OVERVIEW_PADDING_MOBILE } from '@/config/constants';
import { useHasHabitatIntactnessData } from '@/hooks/useHasHabitatIntactnessData';
import { usePathSet } from '@/hooks/usePathSet';
import { usePlot } from '@/pages/shared/hooks/usePlot';
import { useAllPlotsForOrganization } from '@/pages/shared/hooks/usePlotsForOrganization';
import { getGeometriesForPlots } from '@/utils/bounds';
import { buildPath } from '@/utils/buildPath';
import { getPNGIconForLandType } from '@/utils/getPNGIconForLandType';
import { isTestEnv } from '@/utils/isTestEnv';
import { getColorOfPlotType } from '@/utils/plot';

const CURRENT_PLOT_POLYGON_SOURCE_ID = 'current-plot-polygon';
const OTHER_PLOT_POLYGONS_SOURCE_ID = 'other-plot-polygons';
const OTHER_PLOT_MARKERS_SOURCE_ID = 'other-plot-markers';
const CURRENT_PLOT_MARKER_SOURCE_ID = 'current-plot-marker';

export const PlotMapWidget = withSuspenseBoundary(
  () => {
    const plot = usePlot().data;
    return (
      <Stack className='h-[60vh] animate-in fade-in'>
        <PlotMap plot={plot} layers={LAYER_OPTIONS} />
      </Stack>
    );
  },
  <Loader className='min-h-[300px]' />,
);

export const PlotMap = ({ plot, layers }: { plot: Plot; layers?: HeatMapLayerProps[] }) => {
  const hasRadiusContext = !!useContext(MapRadiusContext);

  switch (hasRadiusContext) {
    case true:
      return <PlotMapWithRadius plot={plot} layers={layers} />;
    case false:
    default:
      return <PlotMapBase plot={plot} radius={null} layers={layers} />;
  }
};

const PlotMapWithRadius = ({ plot, layers }: { plot: Plot; layers?: HeatMapLayerProps[] }) => {
  const { radius } = useMapRadiusContext();

  return <PlotMapBase plot={plot} radius={radius} layers={layers} />;
};

const PlotMapBase = withSuspenseBoundary(
  ({ plot, radius, layers }: { plot: Plot; radius: RadiusType | null; layers?: HeatMapLayerProps[] }) => {
    const [isMapReady, setIsMapReady] = useState(isTestEnv);
    const [mapStyle, setMapStyle] = useState(DEFAULT_MAP_STYLE);
    const [isHeatmapLayerLoaded, setIsHeatmapLayerLoaded] = useState(false);
    const [areLayersLoaded, setAreLayersLoaded] = useState(false);
    const [clickedPoint, setClickedPoint] = useState<{
      x: number | undefined;
      y: number | undefined;
      latitude: number | undefined;
      longitude: number | undefined;
    }>({
      latitude: undefined,
      longitude: undefined,
      x: undefined,
      y: undefined,
    });

    const pathSet = usePathSet();
    const { t } = useTranslation();

    const controlsForm = useControlsForm({
      defaultValues: {
        year: `2020`,
        layers: [],
        radius: radius ?? NO_RADIUS,
        habitatIntactnessThreats: [],
        layerOpacity: getDefaultOpacityByLayer(),
        mapBackground: DEFAULT_MAP_BACKGROUND,
        plotOverviewShape: DEFAULT_PLOT_OVERVIEW_SHAPE,
      },
    });

    useEffect(() => {
      if (radius !== null) {
        controlsForm.setValue('radius', radius);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [radius]);

    const [selectedLayers, year] = controlsForm.watch(['layers', 'year']);
    const plotOverviewShape = controlsForm.watch('plotOverviewShape');
    const showFillLayer = plotOverviewShape === PLOT_OVERVIEW_SHAPE.polygon;
    const mapRef = useRef<MapRef | null>(null);

    useEffect(() => {
      const hasMapLoaded = !!(
        mapRef.current &&
        mapRef.current?.isStyleLoaded?.() &&
        mapRef.current?.areTilesLoaded?.() &&
        mapRef.current?.loaded?.()
      );
      setAreLayersLoaded(hasMapLoaded);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLayers, mapStyle, layers, mapRef.current]);

    const geometries = useMemo(() => getGeometriesForPlots([plot]), [plot]);
    const { bounds } = useBoundingBox({
      geometries,
      padding: MAP_OVERVIEW_PADDING_MOBILE,
    });

    const mapRefCallback = useCallback((ref: MapRef | null) => {
      if (ref === null) return;
      mapRef.current = ref;
      const map = ref;

      /**
       * Once the map is loaded, we load the icon PNGs so
       * that they can be referenced by the `iconLayer` layer.
       */
      Object.values(PlotType).forEach((plotType: PlotType) => {
        const loadImage = () => {
          if (!map.hasImage?.(`${plotType}-icon`)) {
            map.loadImage?.(getPNGIconForLandType(plotType), (error, image) => {
              if (error || image === undefined) throw error;
              map.addImage(`${plotType}-icon`, image, { sdf: true });
            });
          }
        };

        loadImage();
      });
    }, []);

    const hasHabitatIntactnessData = useHasHabitatIntactnessData();
    const isHabitatIntactnessSelected = selectedLayers.includes(MAP_LAYER_FACTS.habitat_intactness_raster_urls);
    const renderInteractiveMapLink = hasHabitatIntactnessData && isHabitatIntactnessSelected;

    /**
     * Explode the cluster and zoom in when a cluster is clicked.
     */
    const handleClusterMarkerClick = (feature: Feature<Point>, clusterId: number) => {
      if (!mapRef.current) return;

      const mapboxSource = mapRef.current.getSource(OTHER_PLOT_MARKERS_SOURCE_ID) as GeoJSONSource;

      mapboxSource.getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return;
        if (!mapRef.current) return;

        mapRef.current.easeTo({
          center: feature.geometry.coordinates as LngLatLike,
          zoom,
          duration: 500,
        });
      });
    };

    const handleMapClick = (event: MapLayerMouseEvent) => {
      setClickedPoint({
        x: event?.point?.x,
        y: event?.point?.y,
        longitude: event?.lngLat?.lng,
        latitude: event?.lngLat?.lat,
      });

      const feature = event.features?.[0];

      if (!feature) return;

      const clusterId = feature.properties?.cluster_id;

      if (clusterId) {
        handleClusterMarkerClick(feature as Feature<Point>, clusterId);
      }
    };

    /**
     * Show pointer when hovering over a cluster marker.
     */
    const handleMapMouseMove = (event: MapLayerMouseEvent) => {
      const map = mapRef.current;

      if (!map) return;

      const feature = event.features?.[0];

      if (!feature) {
        map.getCanvas().style.cursor = 'initial';
      } else {
        map.getCanvas().style.cursor = 'pointer';
      }
    };

    const handleMapLoad = () => {
      setIsMapReady(true);

      const [formRadius] = controlsForm.watch(['radius']);
      initializeMapZoom({ bounds, mapRef: mapRef.current, radius: formRadius });
    };

    const currentPlotMarker = featureCollection([
      {
        ...centerOfMass(plot.multi_polygon, {
          properties: {
            plot,
            name: plot.name,
            landType: plot.type,
            icon: `${plot.type}-icon`,
          },
        }),
      },
    ]);

    const currentPlotPolygon =
      plot.approximation_method === 'circle'
        ? undefined
        : featureCollection([
            turfFeature(plot.multi_polygon, {
              plot,
              color: getColorOfPlotType(plot.type),
            }),
          ]);

    const displayLayerTooltip =
      clickedPoint.x &&
      clickedPoint.y &&
      clickedPoint.longitude &&
      clickedPoint.latitude &&
      !selectedLayers?.includes('none') &&
      !selectedLayers?.includes('unknown') &&
      areLayersLoaded &&
      mapRef.current;

    return (
      <div className='h-[187px] w-full overflow-hidden rounded-3xl bg-primary-50 sm:h-[494px] md:h-full'>
        <Form {...controlsForm}>
          <Map
            ref={mapRefCallback}
            cooperativeGestures
            initialViewState={{
              bounds: bounds as LngLatBoundsLike,
              fitBoundsOptions: { padding: MAP_OVERVIEW_PADDING_MOBILE },
            }}
            onLoad={handleMapLoad}
            onClick={handleMapClick}
            onMouseMove={handleMapMouseMove}
            onData={() => setIsHeatmapLayerLoaded(!!mapRef.current?.getLayer(INTERACTIVE_HEATMAP_LAYER_ID))}
            interactiveLayerIds={[
              clusterLayer.id as string,
              clusterCountLayer.id as string,
              ...(isHeatmapLayerLoaded ? [INTERACTIVE_HEATMAP_LAYER_ID] : []),
            ]}
            style={{ opacity: isMapReady ? 1 : 0, transition: 'opacity 0.2s' }}
            data-testid='plot-map'
            mapStyle={mapStyle}
          >
            <OtherPlotLayers plotId={plot.id} />
            <Source id={CURRENT_PLOT_POLYGON_SOURCE_ID} type='geojson' data={currentPlotPolygon}>
              <Layer {...getCurrentPlotPolygonOutlineLayerDark(plotOverviewShape)} />
            </Source>
            <Source id={CURRENT_PLOT_POLYGON_SOURCE_ID} type='geojson' data={currentPlotPolygon}>
              <Layer {...getCurrentPlotPolygonOutlineLayerLight(plotOverviewShape)} />
              {showFillLayer && <Layer {...currentPlotPolygonFillLayer} />}
            </Source>
            {layers && layers.length > 0 && <HeatMapLayer setMapStyle={setMapStyle} layers={layers} />}
            <Source id={CURRENT_PLOT_MARKER_SOURCE_ID} type='geojson' data={currentPlotMarker}>
              <Layer {...currentPlotPointLayer} />
              <Layer {...currentPlotIconLayer} />
            </Source>
            <MapSettings radius={radius} bounds={bounds} mapRef={mapRef.current} plotMarkers={currentPlotMarker} />
            {renderInteractiveMapLink && (
              <ButtonLink
                to={buildPath(pathSet.plotInteractiveMap, { pathParams: { plotId: plot.id } })}
                className='absolute bottom-6 right-5'
                color='white'
                size='small'
                leftAdornment={<RiRoadMapLine size={20} />}
              >
                {t('shared.projects.project.conservation.overview.labels.interactiveMap')}
              </ButtonLink>
            )}
            {displayLayerTooltip && (
              <LayerTooltip
                onClose={() =>
                  setClickedPoint({
                    x: undefined,
                    y: undefined,
                    longitude: undefined,
                    latitude: undefined,
                  })
                }
                layers={selectedLayers}
                year={year}
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                mapRef={mapRef!}
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                x={clickedPoint.x!}
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                y={clickedPoint.y!}
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                longitude={clickedPoint.longitude!}
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                latitude={clickedPoint.latitude!}
              />
            )}
          </Map>
        </Form>
      </div>
    );
  },
  <Loader className='min-h-[300px]' />,
);

const OtherPlotLayers = withSuspenseBoundary(({ plotId }: { plotId: string }) => {
  const plotGroupWithoutGrouping = usePlotGroupWithoutGrouping();

  const plots: Plot[] = useAllPlotsForOrganization(
    undefined,
    undefined,
    undefined,
    plotGroupWithoutGrouping?.id ?? undefined,
  ).data.results;

  const otherPlots = plots.filter((p) => p.id !== plotId);

  const controlsForm = useControlsContext();
  const plotOverviewShape = controlsForm.watch('plotOverviewShape');
  const showFillLayer = plotOverviewShape === PLOT_OVERVIEW_SHAPE.polygon;

  /**
   * We have separate collections for the points (plotMarkers) and polygons
   * (plotPolygons). We supply them to separate sources with IDs
   * POLYGONS_SOURCE_ID and MARKERS_SOURCE_ID.
   *
   * The `plotMarkers` are clustered using Mapbox's clustering feature, so that
   * the markers don't look crowded when there are several markers close
   * together.
   */
  const otherPlotMarkers = featureCollection(
    otherPlots.map((plot, index) => ({
      ...centerOfMass(plot.multi_polygon, {
        properties: {
          plot,
          name: plot.name,
          landType: plot.type,
          icon: `${plot.type}-icon`,
        },
      }),
      /**
       * We use index instead of plot.id because Mapbox doesn't allow
       * alpha-numeric feature IDs. Also, 0 is not a valid feature ID.
       * https://github.com/mapbox/mapbox-gl-js/issues/7986#issuecomment-469381664
       */
      id: index + 1,
    })),
  );
  const otherPlotPolygons = featureCollection(
    otherPlots
      .filter((plot) => plot.approximation_method !== 'circle')
      .map((plot, index) =>
        turfFeature(
          plot.multi_polygon,
          { plot, color: getColorOfPlotType(plot.type) },
          /**
           * We use index instead of plot.id because Mapbox doesn't allow
           * alpha-numeric feature IDs. Also, 0 is not a valid feature ID.
           * https://github.com/mapbox/mapbox-gl-js/issues/7986#issuecomment-469381664
           */
          { id: index + 1 },
        ),
      ),
  );

  return (
    <>
      <Source id={OTHER_PLOT_POLYGONS_SOURCE_ID} type='geojson' data={otherPlotPolygons}>
        <Layer {...getOtherPlotPolygonsOutlineLayerDark(plotOverviewShape)} />
      </Source>
      <Source id={OTHER_PLOT_POLYGONS_SOURCE_ID} type='geojson' data={otherPlotPolygons}>
        <Layer {...getOtherPlotPolygonsOutlineLayerLight(plotOverviewShape)} />
        {showFillLayer && <Layer {...otherPlotPolygonsFillLayer} />}
      </Source>

      <Source id={OTHER_PLOT_MARKERS_SOURCE_ID} type='geojson' data={otherPlotMarkers} cluster={true}>
        <Layer {...clusterLayer} />
        <Layer {...clusterCountLayer} />
        <Layer {...otherPlotPointsLayer} />
        <Layer {...otherPlotIconsLayer} />
      </Source>
    </>
  );
}, null);

const POLYGON_BORDER_FOR_PLOT_OVERVIEW_BORDER = {
  'line-width': 6,
  'line-opacity': 1,
  'line-color': 'rgb(255, 255, 0)',
} as const;

const OTHER_PLOT_POLYGONS_OUTLINE_LAYER_ID = 'other-plot-polygons-outline';
const CURRENT_PLOT_POLYGON_OUTLINE_LAYER_ID = 'current-plot-polygon-outline';

const getOtherPlotPolygonsOutlineLayerLight = (plotOverviewShape: PlotOverviewShape): LayerProps => {
  switch (plotOverviewShape) {
    case PLOT_OVERVIEW_SHAPE.border:
      return {
        id: OTHER_PLOT_POLYGONS_OUTLINE_LAYER_ID,
        type: 'line',
        paint: POLYGON_BORDER_FOR_PLOT_OVERVIEW_BORDER,
      };
    case PLOT_OVERVIEW_SHAPE.polygon:
    default:
      return {
        id: OTHER_PLOT_POLYGONS_OUTLINE_LAYER_ID,
        type: 'line',
        paint: {
          'line-width': 3,
          'line-opacity': 0.25,
          'line-color': 'rgb(255, 255, 255)',
        },
      };
  }
};

const getOtherPlotPolygonsOutlineLayerDark = (plotOverviewShape: PlotOverviewShape): LayerProps => {
  switch (plotOverviewShape) {
    case PLOT_OVERVIEW_SHAPE.border:
      return {
        id: OTHER_PLOT_POLYGONS_OUTLINE_LAYER_ID,
        type: 'line',
        paint: POLYGON_BORDER_FOR_PLOT_OVERVIEW_BORDER,
      };
    case PLOT_OVERVIEW_SHAPE.polygon:
    default:
      return {
        id: OTHER_PLOT_POLYGONS_OUTLINE_LAYER_ID,
        type: 'line',
        paint: {
          'line-width': 3,
          'line-opacity': 0.25,
          'line-color': 'rgb(0, 0, 0)',
        },
      };
  }
};

const otherPlotPolygonsFillLayer: LayerProps = {
  id: 'other-plot-polygons-fill',
  type: 'fill',
  paint: {
    'fill-color': ['get', 'color'],
    'fill-opacity': 0.25,
  },
};

const clusterLayer: LayerProps = {
  id: 'cluster',
  type: 'circle',

  filter: ['has', 'point_count'],
  paint: {
    'circle-color': '#ffffff',
    'circle-stroke-width': 15,
    'circle-stroke-color': '#ffffff',
    'circle-stroke-opacity': 0.4,
    'circle-radius': 20,
    'circle-opacity': 0.25,
  },
};

const clusterCountLayer: LayerProps = {
  id: 'cluster-count',
  type: 'symbol',
  filter: ['has', 'point_count'],
  layout: {
    'text-field': '{point_count_abbreviated}',
    'text-size': 12,
  },
  paint: {
    'text-opacity': 1,
    'text-color': '#ffffff',
    'text-halo-color': '#505050',
    'text-halo-width': 0.8,
  },
};

const otherPlotPointsLayer: LayerProps = {
  id: 'other-plot-points',
  type: 'circle',
  filter: ['!', ['has', 'point_count']],
  paint: {
    'circle-color': '#ffffff',
    'circle-stroke-width': 10,
    'circle-stroke-color': '#ffffff',
    'circle-stroke-opacity': 0.4,
    'circle-radius': 15,
    'circle-opacity': 0.25,
  },
};

const otherPlotIconsLayer: LayerProps = {
  id: 'other-plot-icons',
  type: 'symbol',
  filter: ['!', ['has', 'point_count']],
  layout: {
    'icon-image': ['get', 'icon'],
    'icon-size': 0.5,
    'text-field': ['get', 'name'],
    'text-offset': [0, 3],
    'text-anchor': 'bottom',
    'text-size': 12,
  },
  paint: {
    'text-color': '#ffffff',
    'text-halo-color': '#000000',
    'text-halo-width': 2,
    'icon-opacity': 0.25,
    'text-opacity': 0.25,
  },
};

const getCurrentPlotPolygonOutlineLayerLight = (plotOverviewShape: PlotOverviewShape): LayerProps => {
  switch (plotOverviewShape) {
    case PLOT_OVERVIEW_SHAPE.border:
      return {
        id: CURRENT_PLOT_POLYGON_OUTLINE_LAYER_ID,
        type: 'line',
        paint: POLYGON_BORDER_FOR_PLOT_OVERVIEW_BORDER,
      };
    case PLOT_OVERVIEW_SHAPE.polygon:
    default:
      return {
        id: CURRENT_PLOT_POLYGON_OUTLINE_LAYER_ID,
        type: 'line',
        paint: {
          'line-width': 3,
          'line-opacity': 0.6,
          'line-color': 'rgb(255, 255, 255)',
        },
      };
  }
};

const getCurrentPlotPolygonOutlineLayerDark = (plotOverviewShape: PlotOverviewShape): LayerProps => {
  switch (plotOverviewShape) {
    case PLOT_OVERVIEW_SHAPE.border:
      return {
        id: CURRENT_PLOT_POLYGON_OUTLINE_LAYER_ID,
        type: 'line',
        paint: POLYGON_BORDER_FOR_PLOT_OVERVIEW_BORDER,
      };
    case PLOT_OVERVIEW_SHAPE.polygon:
    default:
      return {
        id: CURRENT_PLOT_POLYGON_OUTLINE_LAYER_ID,
        type: 'line',
        paint: {
          'line-width': 3,
          'line-opacity': 0.6,
          'line-color': 'rgb(0, 0, 0)',
        },
      };
  }
};

const currentPlotPolygonFillLayer: LayerProps = {
  id: 'current-plot-polygon-fill',
  type: 'fill',
  paint: {
    'fill-color': ['get', 'color'],
    'fill-opacity': 0.6,
  },
};

const currentPlotPointLayer: LayerProps = {
  id: 'current-plot-point',
  type: 'circle',
  paint: {
    'circle-color': '#ffffff',
    'circle-stroke-width': 10,
    'circle-stroke-color': '#ffffff',
    'circle-stroke-opacity': 0.4,
    'circle-radius': 15,
  },
};

const currentPlotIconLayer: LayerProps = {
  id: 'current-plot-icon',
  type: 'symbol',
  layout: {
    'icon-image': ['get', 'icon'],
    'icon-size': 0.5,
    'text-field': ['get', 'name'],
    'text-offset': [0, 3],
    'text-anchor': 'bottom',
    'text-size': 12,
  },
  paint: {
    'text-color': '#ffffff',
    'text-halo-color': '#000000',
    'text-halo-width': 2,
  },
};
